import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  Search,
  RefreshCcw,
  Loader2,
  Info,
  TrendingUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Filter,
  Download,
} from "lucide-react";
import * as XLSX from "xlsx";
import { exportStyledXlsx } from "../lib/excelExport";

const Research = () => {
  const { api } = useAuth();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAllSchemes, setTotalAllSchemes] = useState(0);
  const [schemeBreakdown, setSchemeBreakdown] = useState({
    growth_with_category: 0,
    idcw_with_category: 0,
    total_with_category: 0,
    missing_category: 0
  });
  const [searchQuery, setSearchQuery] = useState("");
  // Multi-select filters (arrays)
  const [selectedAmcs, setSelectedAmcs] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]); // Equity/Debt/Hybrid
  const [selectedFundCategories, setSelectedFundCategories] = useState([]); // AMFI categories
  const [selectedTenures, setSelectedTenures] = useState([]); // Tenure ranges
  const [optionFilter, setOptionFilter] = useState("regular"); // "direct", "regular", or "both"
  const [quickFilter, setQuickFilter] = useState(null); // Quick filter capsule selection
  const [topPercentile, setTopPercentile] = useState(null); // Top percentile filter: 25, 50, 75, 100
  const [amcList, setAmcList] = useState([]);
  const [fundCategories, setFundCategories] = useState([]); // AMFI categories
  const [loadingNav, setLoadingNav] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "xirr3y", direction: "desc" }); // Default sort by 3Y XIRR
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  // Quick filter capsule options - removed, replaced by percentile selector
  
  // Top percentile options
  const topPercentileOptions = [
    { value: 25, label: "Top 25%" },
    { value: 50, label: "Top 50%" },
    { value: 75, label: "Top 75%" },
    { value: 100, label: "All (100%)" },
  ];

  // Tenure filter options
  const tenureOptions = [
    { value: "0-1", label: "< 1 Year", min: 0, max: 1 },
    { value: "1-3", label: "1-3 Years", min: 1, max: 3 },
    { value: "3-5", label: "3-5 Years", min: 3, max: 5 },
    { value: "5-10", label: "5-10 Years", min: 5, max: 10 },
    { value: "10+", label: "10+ Years", min: 10, max: Infinity },
  ];

  // Reference date for XIRR calculation - Jan 31, 2018
  const XIRR_BASE_DATE = new Date(2018, 0, 31); // Jan 31, 2018

  // Calculate years from Jan 31, 2018 to today
  const calculateXirrTenure = () => {
    const today = new Date();
    const years = (today - XIRR_BASE_DATE) / (1000 * 60 * 60 * 24 * 365.25);
    return years;
  };

  const fetchSchemes = useCallback(async () => {
    setLoading(true);
    setPage(1); // Reset to first page on new fetch
    try {
      const params = new URLSearchParams({
        skip: "0",
        limit: "20000",  // Fetch all schemes for proper XIRR sorting
      });
      
      if (searchQuery) params.append("search", searchQuery);
      // Multi-select: send first selected AMC (backend filters server-side)
      // Client-side filtering handles the rest
      
      const response = await api.get(`/schemes?${params.toString()}`);
      const schemesData = response.data.schemes || [];
      setSchemes(schemesData);
      setTotalCount(response.data.total || 0);
      setTotalAllSchemes(response.data.total_all || response.data.total || 0);
      if (response.data.breakdown) {
        setSchemeBreakdown(response.data.breakdown);
      }
      
      // NAV data is now included directly in scheme_master (current_nav, nav_3y, nav_5y)
      // No need to fetch separately - remove AMFI API dependency
      setLoadingNav(false);
    } catch (error) {
      toast.error("Failed to fetch schemes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api, searchQuery]);

  // NAV data is now fetched by the NAV Update Bot and stored in scheme_master
  // Fields available: current_nav, current_nav_date, nav_3y, nav_3y_date, nav_5y, nav_5y_date

  const fetchFilters = useCallback(async () => {
    try {
      const response = await api.get("/schemes/filters");
      setAmcList(response.data.amc_codes || []);
      setFundCategories(response.data.fund_categories || []);
    } catch (error) {
      console.error("Failed to fetch filters", error);
    }
  }, [api]);

  useEffect(() => {
    fetchSchemes();
  }, [fetchSchemes]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Calculate tenure from start date (returns years and months)
  const calculateTenure = (startDate) => {
    if (!startDate) return { years: null, months: null, display: "-" };
    try {
      const start = new Date(startDate);
      if (isNaN(start.getTime())) return { years: null, months: null, display: "-" };
      const now = new Date();
      
      let years = now.getFullYear() - start.getFullYear();
      let months = now.getMonth() - start.getMonth();
      
      if (months < 0) {
        years--;
        months += 12;
      }
      
      // Adjust for day of month
      if (now.getDate() < start.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }
      
      const totalYears = years + (months / 12);
      const display = `${years}y ${months}m`;
      
      return { years: totalYears, months, display };
    } catch {
      return { years: null, months: null, display: "-" };
    }
  };

  // Calculate years between two dates
  const calculateYearsBetweenDates = (startDateStr, endDateStr) => {
    try {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      const diffMs = end - start;
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      return diffDays / 365.25; // Account for leap years
    } catch {
      return null;
    }
  };

  // Calculate XIRR (CAGR) for a given period
  // period: 'inception' (since start), '3y' (3 years), '5y' (5 years)
  // XIRR = ((Sale NAV / Purchase NAV) ^ (1 / years)) - 1
  // Where years = actual days between purchase and sale dates / 365.25
  // NAV data comes directly from scheme_master (populated by NAV Update Bot)
  const calculateXIRRForPeriod = (scheme, period = 'inception') => {
    // Only show for Growth schemes, not IDCW
    if (!scheme.scheme_name) return null;
    const nameUpper = scheme.scheme_name.toUpperCase();
    if (nameUpper.includes("IDCW") || nameUpper.includes("DIVIDEND")) {
      return null;
    }
    
    // Get current NAV from scheme_master (populated by NAV Update Bot)
    const currentNav = scheme.current_nav;
    if (!currentNav) return null;

    const tenure = calculateTenure(scheme.start_date);
    const tenureYears = tenure.years || 0;
    
    try {
      const faceValue = scheme.face_value || 10;
      
      if (period === '3y') {
        // 3 Year XIRR: ONLY show when we have actual historical NAV
        if (tenureYears > 0 && tenureYears < 3) return null; // Scheme not old enough
        
        // Use 3Y NAV from scheme_master
        const nav3y = scheme.nav_3y;
        if (nav3y && nav3y > 0) {
          const cagr = (Math.pow(currentNav / nav3y, 1 / 3) - 1) * 100;
          return parseFloat(cagr.toFixed(2));
        }
        return null;
        
      } else if (period === '5y') {
        // 5 Year XIRR: ONLY show when we have actual historical NAV
        if (tenureYears > 0 && tenureYears < 5) return null; // Scheme not old enough
        
        // Use 5Y NAV from scheme_master
        const nav5y = scheme.nav_5y;
        if (nav5y && nav5y > 0) {
          const cagr = (Math.pow(currentNav / nav5y, 1 / 5) - 1) * 100;
          return parseFloat(cagr.toFixed(2));
        }
        return null;
        
      } else {
        // Since inception - use actual tenure
        if (tenureYears <= 0) return null;
        
        // Use face value for inception XIRR
        const cagr = (Math.pow(currentNav / faceValue, 1 / tenureYears) - 1) * 100;
        return parseFloat(cagr.toFixed(2));
      }
    } catch {
      return null;
    }
  };

  // Calculate Absolute Returns for a given period
  // Returns an object with { total: totalAbsolute, avg: averagePerYear }
  // NAV data comes directly from scheme_master (populated by NAV Update Bot)
  const calculateAbsoluteForPeriod = (scheme, period = 'inception') => {
    // Only show for Growth schemes, not IDCW
    if (!scheme.scheme_name) return null;
    const nameUpper = scheme.scheme_name.toUpperCase();
    if (nameUpper.includes("IDCW") || nameUpper.includes("DIVIDEND")) {
      return null;
    }
    
    // Get current NAV from scheme_master
    const currentNav = scheme.current_nav;
    if (!currentNav) return null;

    const tenure = calculateTenure(scheme.start_date);
    const tenureYears = tenure.years || 0;
    
    try {
      const faceValue = scheme.face_value || 10;
      
      let initialNav = faceValue;
      let periodYears = tenureYears;
      
      if (period === '3y') {
        if (tenureYears > 0 && tenureYears < 3) return null;
        // Use 3Y NAV from scheme_master
        const nav3y = scheme.nav_3y;
        if (nav3y && nav3y > 0) {
          initialNav = nav3y;
          periodYears = 3;
        } else {
          return null;
        }
      } else if (period === '5y') {
        if (tenureYears > 0 && tenureYears < 5) return null;
        // Use 5Y NAV from scheme_master
        const nav5y = scheme.nav_5y;
        if (nav5y && nav5y > 0) {
          initialNav = nav5y;
          periodYears = 5;
        } else {
          return null;
        }
      }
      // Inception uses face_value as initialNav (already set)
      
      if (!initialNav || initialNav <= 0 || periodYears <= 0) return null;
      
      const totalAbsolute = ((currentNav - initialNav) / initialNav) * 100;
      const avgAbsolute = totalAbsolute / periodYears;
      
      return {
        total: parseFloat(totalAbsolute.toFixed(2)),
        avg: parseFloat(avgAbsolute.toFixed(2))
      };
    } catch {
      return null;
    }
  };

  // Legacy functions for backward compatibility
  const calculateXIRR = (scheme) => calculateXIRRForPeriod(scheme, 'inception');
  const calculateAbsoluteReturns = (scheme) => {
    const result = calculateAbsoluteForPeriod(scheme, 'inception');
    return result ? result.total : null;
  };

  // Format date for display as dd-mm-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "-";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return "-";
    }
  };

  // Parse NAV date format (DD-Mon-YYYY like "06-Mar-2026")
  const parseNavDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const months = {
        'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
        'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
      };
      const parts = dateStr.split('-');
      if (parts.length !== 3) return null;
      const day = parseInt(parts[0]);
      const month = months[parts[1]];
      const year = parseInt(parts[2]);
      if (isNaN(day) || month === undefined || isNaN(year)) return null;
      return new Date(year, month, day);
    } catch {
      return null;
    }
  };

  // Calculate working days between two dates (excluding weekends)
  const getWorkingDaysDiff = (startDate, endDate) => {
    let count = 0;
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    
    while (current < end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday or Saturday
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  // Check if NAV is stale (older than 3-4 working days)
  // FoF funds take an extra day to declare NAV, so they get more leeway
  const isNavStale = (navDateStr, isFoF = false) => {
    if (!navDateStr) return false;
    const navDate = parseNavDate(navDateStr);
    if (!navDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    navDate.setHours(0, 0, 0, 0);
    
    // Calculate working days difference
    const workingDays = getWorkingDaysDiff(navDate, today);
    
    // FoF funds take 1 extra day, so allow 4 working days
    // Regular funds allow 3 working days
    const maxAcceptableWorkingDays = isFoF ? 4 : 3;
    
    // NAV is stale only if older than acceptable working days
    return workingDays > maxAcceptableWorkingDays;
  };

  // Format option text to Title Case / Camel Case
  const formatOptionText = (text) => {
    if (!text) return text;
    
    // Handle AMC codes - clean up the format
    if (text.includes('_MF') || text.includes('MUTUALFUND')) {
      let formatted = text
        .replace(/_MF$/i, '')
        .replace(/MUTUALFUND/gi, ' Mutual Fund')
        .replace(/_/g, ' ')
        .trim();
      
      // Convert to title case
      formatted = formatted
        .toLowerCase()
        .split(' ')
        .filter(w => w)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Add "Mutual Fund" suffix if not present
      if (!formatted.toLowerCase().includes('mutual fund')) {
        formatted += ' Mutual Fund';
      }
      
      return formatted;
    }
    
    // Handle simple words like "equity", "debt", "hybrid"
    if (text.length < 20 && !text.includes(' ') && !text.includes('_')) {
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }
    
    // Handle fund categories with proper casing
    return text
      .split(' ')
      .map((word, idx) => {
        // Keep short words lowercase unless at start
        const lowerWord = word.toLowerCase();
        if (idx > 0 && ['and', 'of', 'the', 'for', 's'].includes(lowerWord)) {
          return lowerWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  // Extract short category name (part after the "-")
  const getShortCategory = (fullCategory) => {
    if (!fullCategory) return null;
    const parts = fullCategory.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ').trim() : fullCategory;
  };

  // Sorting logic
  const handleSort = (key) => {
    // 3Y and 5Y columns (XIRR and Absolute) should only sort descending (highest to lowest)
    const descendingOnlyColumns = ["xirr3y", "xirr5y", "abs3y", "abs5y"];
    
    if (descendingOnlyColumns.includes(key)) {
      // Always sort descending for these columns
      setSortConfig({ key, direction: "desc" });
    } else {
      // Toggle sort direction for other columns
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
      }));
    }
    setPage(1); // Reset to first page on sort change
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [optionFilter, selectedCategories, selectedFundCategories, selectedAmcs, selectedTenures, topPercentile]);

  // Helper to check if scheme is Growth type
  const isGrowthScheme = (schemeName) => {
    if (!schemeName) return false;
    const nameUpper = schemeName.toUpperCase();
    return nameUpper.includes("GROWTH") && !nameUpper.includes("IDCW") && !nameUpper.includes("DIVIDEND");
  };

  // Helper to get category from scheme type
  const getCategory = (schemeType) => {
    if (!schemeType) return "other";
    const type = schemeType.toUpperCase();
    if (type === "EQUITY" || type === "ELSS") return "equity";
    if (type === "DEBT" || type === "BOND" || type === "GILT" || type === "LIQUID" || type === "OVERNIGHT" || type === "INCOME" || type === "MIP") return "debt";
    if (type.includes("HYBRID") || type === "FOF") return "hybrid";
    return "other";
  };

  const getSortedSchemes = () => {
    // Check for pre-calculated returns from API
    const schemesWithPreCalc = schemes.filter(s => s.xirr_3y !== undefined || s.xirr_5y !== undefined).length;
    const usingPreCalc = schemesWithPreCalc > 100; // Use pre-calc if significant data available
    
    if (usingPreCalc) {
      console.log(`Using pre-calculated returns: ${schemesWithPreCalc} schemes have 3Y/5Y data`);
    } else {
      // NAV data now comes directly from scheme_master (populated by NAV Update Bot)
      const schemesWithNav = schemes.filter(s => s.current_nav).length;
      const schemesWith3yNav = schemes.filter(s => s.nav_3y).length;
      console.log(`NAV data: ${schemesWithNav} schemes with current NAV, ${schemesWith3yNav} with 3Y NAV`);
    }
    
    // First apply client-side filters (multi-select)
    let filteredSchemes = schemes.filter(scheme => {
      // Direct/Regular plan filter - check scheme name
      if (optionFilter === "direct") {
        const nameUpper = (scheme.scheme_name || "").toUpperCase();
        if (!nameUpper.includes("DIRECT")) return false;
      } else if (optionFilter === "regular") {
        const nameUpper = (scheme.scheme_name || "").toUpperCase();
        if (nameUpper.includes("DIRECT")) return false;
      }
      
      // AMC filter (multi-select)
      if (selectedAmcs.length > 0) {
        if (!selectedAmcs.includes(scheme.amc_code)) return false;
      }
      
      // Category filter (Equity/Debt/Hybrid) - multi-select
      if (selectedCategories.length > 0) {
        const derivedCat = getCategory(scheme.scheme_type);
        if (!selectedCategories.includes(derivedCat)) return false;
      }
      
      // Fund Category filter (AMFI Scheme Category) - multi-select
      if (selectedFundCategories.length > 0) {
        if (!selectedFundCategories.includes(scheme.category)) return false;
      }
      
      // Tenure filter - applied after calculating tenure
      // We'll filter this after calculations
      
      return true;
    });

    const schemesWithCalculations = filteredSchemes.map(scheme => {
      const tenure = calculateTenure(scheme.start_date);
      const derivedCategory = getCategory(scheme.scheme_type); // For Equity/Debt/Hybrid filter
      
      // Use pre-calculated returns from API if available, otherwise fallback to on-demand calculation
      let xirr3y, xirr5y, abs3yTotal, abs3yAvg, abs5yTotal, abs5yAvg;
      
      if (scheme.xirr_3y !== undefined) {
        // Use pre-calculated values from backend
        xirr3y = scheme.xirr_3y;
        abs3yTotal = scheme.abs_3y_total;
        abs3yAvg = scheme.abs_3y_avg;
      } else {
        // Fallback to on-demand calculation
        const abs3yData = calculateAbsoluteForPeriod(scheme, '3y');
        xirr3y = calculateXIRRForPeriod(scheme, '3y');
        abs3yTotal = abs3yData ? abs3yData.total : null;
        abs3yAvg = abs3yData ? abs3yData.avg : null;
      }
      
      if (scheme.xirr_5y !== undefined) {
        // Use pre-calculated values from backend
        xirr5y = scheme.xirr_5y;
        abs5yTotal = scheme.abs_5y_total;
        abs5yAvg = scheme.abs_5y_avg;
      } else {
        // Fallback to on-demand calculation
        const abs5yData = calculateAbsoluteForPeriod(scheme, '5y');
        xirr5y = calculateXIRRForPeriod(scheme, '5y');
        abs5yTotal = abs5yData ? abs5yData.total : null;
        abs5yAvg = abs5yData ? abs5yData.avg : null;
      }
      
      // Calculate absolute returns for inception (always on-demand as it varies)
      const absInception = calculateAbsoluteForPeriod(scheme, 'inception');
      
      return {
        ...scheme,
        tenure: tenure.years,
        tenureDisplay: tenure.display,
        xirr: calculateXIRR(scheme),
        absoluteReturns: absInception ? absInception.total : null,
        absoluteReturnsAvg: absInception ? absInception.avg : null,
        // 3-year returns (pre-calculated or on-demand)
        xirr3y,
        abs3yTotal,
        abs3yAvg,
        // 5-year returns (pre-calculated or on-demand)
        xirr5y,
        abs5yTotal,
        abs5yAvg,
        // NAV data comes directly from scheme_master
        nav: scheme.returns_current_nav || scheme.current_nav || null,
        navDate: scheme.returns_current_date || scheme.current_nav_date || null,
        derivedCategory: derivedCategory, // Keep derived for filtering
        // category comes from API (AMFI data) - don't overwrite it
        isGrowth: isGrowthScheme(scheme.scheme_name),
      };
    });

    // Apply tenure filter after calculations (since tenure is calculated)
    let finalSchemes = schemesWithCalculations;
    if (selectedTenures.length > 0) {
      finalSchemes = schemesWithCalculations.filter(scheme => {
        const tenureYears = scheme.tenure || 0;
        return selectedTenures.some(range => {
          const option = tenureOptions.find(o => o.value === range);
          if (!option) return false;
          return tenureYears >= option.min && tenureYears < option.max;
        });
      });
    }

    // Apply top percentile filter (by 3Y XIRR for better relevance)
    if (topPercentile && topPercentile < 100) {
      // Sort by 3Y XIRR descending first (fallback to inception XIRR)
      finalSchemes = finalSchemes.sort((a, b) => {
        const aVal = a.xirr3y !== null ? a.xirr3y : (a.xirr || -999);
        const bVal = b.xirr3y !== null ? b.xirr3y : (b.xirr || -999);
        return bVal - aVal;
      });
      // Keep top X%
      const percentileCount = Math.ceil(finalSchemes.length * (topPercentile / 100));
      finalSchemes = finalSchemes.slice(0, percentileCount);
    }

    return finalSchemes.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case "isin":
          aVal = a.isin || "";
          bVal = b.isin || "";
          break;
        case "scheme_type":
          aVal = a.scheme_type || "";
          bVal = b.scheme_type || "";
          break;
        case "scheme_name":
          aVal = a.scheme_name || "";
          bVal = b.scheme_name || "";
          break;
        case "fund_category":
          aVal = a.category || "";
          bVal = b.category || "";
          break;
        case "start_date":
          aVal = a.start_date ? new Date(a.start_date).getTime() : 0;
          bVal = b.start_date ? new Date(b.start_date).getTime() : 0;
          break;
        case "tenure":
          aVal = a.tenure || 0;
          bVal = b.tenure || 0;
          break;
        case "face_value":
          aVal = a.face_value || 0;
          bVal = b.face_value || 0;
          break;
        case "nav":
          aVal = a.nav || 0;
          bVal = b.nav || 0;
          break;
        case "xirr":
          aVal = a.xirr || -999;
          bVal = b.xirr || -999;
          break;
        case "absoluteReturns":
          aVal = a.absoluteReturnsAvg || -999;
          bVal = b.absoluteReturnsAvg || -999;
          break;
        case "xirr3y":
          // For 3Y sorting, put null values at the end
          aVal = a.xirr3y !== null ? a.xirr3y : -Infinity;
          bVal = b.xirr3y !== null ? b.xirr3y : -Infinity;
          break;
        case "abs3y":
          aVal = a.abs3yAvg !== null ? a.abs3yAvg : -Infinity;
          bVal = b.abs3yAvg !== null ? b.abs3yAvg : -Infinity;
          break;
        case "xirr5y":
          aVal = a.xirr5y !== null ? a.xirr5y : -Infinity;
          bVal = b.xirr5y !== null ? b.xirr5y : -Infinity;
          break;
        case "abs5y":
          aVal = a.abs5yAvg !== null ? a.abs5yAvg : -Infinity;
          bVal = b.abs5yAvg !== null ? b.abs5yAvg : -Infinity;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  const SortIcon = ({ columnKey }) => {
    // 3Y and 5Y columns only sort descending
    const descendingOnlyColumns = ["xirr3y", "xirr5y", "abs3y", "abs5y"];
    
    if (sortConfig.key !== columnKey) {
      // For descending-only columns, show down arrow hint
      if (descendingOnlyColumns.includes(columnKey)) {
        return <ArrowDown className="w-3 h-3 ml-1 opacity-30" />;
      }
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    }
    
    // Active sort
    if (descendingOnlyColumns.includes(columnKey)) {
      return <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
    }
    
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  // Get all sorted/filtered schemes
  const allSortedSchemes = getSortedSchemes();
  
  // Paginate the sorted results
  const totalFiltered = allSortedSchemes.length;
  const totalPages = Math.ceil(totalFiltered / pageSize);
  const paginatedSchemes = allSortedSchemes.slice((page - 1) * pageSize, page * pageSize);

  // Count active filters
  const activeFilterCount = selectedAmcs.length + 
    selectedCategories.length + selectedFundCategories.length + selectedTenures.length + 
    (topPercentile && topPercentile < 100 ? 1 : 0);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedAmcs([]);
    setSelectedCategories([]);
    setSelectedFundCategories([]);
    setSelectedTenures([]);
    setQuickFilter(null);
    setTopPercentile(null);
    setOptionFilter("growth");
    setPage(1);
  };

  // Handle top percentile change
  const handleTopPercentileChange = (value) => {
    if (value === topPercentile) {
      setTopPercentile(null); // Toggle off if same
    } else {
      setTopPercentile(value);
    }
    setPage(1);
  };

  // Download Excel function
  const downloadExcel = () => {
    try {
      // Prepare data for Excel - use all filtered schemes (not just current page)
      const excelData = allSortedSchemes.map((scheme) => ({
        "ISIN": scheme.isin || "",
        "Category": scheme.category || scheme.fund_category || "",
        "Scheme Name": scheme.scheme_name || "",
        "AMC": scheme.amc || "",
        "Scheme Type": scheme.scheme_type || "",
        "Start Date": scheme.startDateDisplay || "",
        "NAV Start Date": scheme.nav_start_date ? formatDate(scheme.nav_start_date) : "",
        "Tenure": scheme.tenureDisplay || "",
        "Face Value": scheme.face_value || "",
        "Current NAV": scheme.nav || "",
        "NAV Date": scheme.navDate || "",
        "Grandfathered NAV (31-Jan-2018)": scheme.nav_grandfathered || "",
        "XIRR % (Inception)": scheme.xirr !== null ? scheme.xirr : "",
        "XIRR % (3 Year)": scheme.xirr3y !== null ? scheme.xirr3y : "",
        "XIRR % (5 Year)": scheme.xirr5y !== null ? scheme.xirr5y : "",
        "Absolute Avg % (Inception)": scheme.absoluteReturnsAvg !== null ? scheme.absoluteReturnsAvg : "",
        "Absolute Total % (Inception)": scheme.absoluteReturns !== null ? scheme.absoluteReturns : "",
        "Absolute Avg % (3 Year)": scheme.abs3yAvg !== null ? scheme.abs3yAvg : "",
        "Absolute Total % (3 Year)": scheme.abs3yTotal !== null ? scheme.abs3yTotal : "",
        "Absolute Avg % (5 Year)": scheme.abs5yAvg !== null ? scheme.abs5yAvg : "",
        "Absolute Total % (5 Year)": scheme.abs5yTotal !== null ? scheme.abs5yTotal : "",
      }));

      // Use shared styled exporter — handles auto column widths, bold/teal
      // header, Indian number format, dates, zebra striping, etc.
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const filename = `MF_Schemes_${dateStr}.xlsx`;
      exportStyledXlsx([{ name: "MF Schemes", rows: excelData }], filename);

      toast.success(`Downloaded ${excelData.length} schemes to ${filename}`);
    } catch (error) {
      console.error("Excel download error:", error);
      toast.error("Failed to download Excel file");
    }
  };

  // Dynamic filter options - each filter depends on ALL other selected filters
  // This allows any filter to be selected first and others will adjust accordingly
  
  const getFilteredCategories = () => {
    let filteredSchemes = schemes;
    
    // Apply Direct/Regular plan filter first
    if (optionFilter === "direct") {
      filteredSchemes = filteredSchemes.filter(s => (s.scheme_name || "").toUpperCase().includes("DIRECT"));
    } else if (optionFilter === "regular") {
      filteredSchemes = filteredSchemes.filter(s => !(s.scheme_name || "").toUpperCase().includes("DIRECT"));
    }
    
    // Filter by selected AMCs
    if (selectedAmcs.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => 
        selectedAmcs.includes(scheme.amc_code)
      );
    }
    
    // Filter by selected fund categories
    if (selectedFundCategories.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => 
        selectedFundCategories.includes(scheme.category)
      );
    }
    
    // Extract unique categories from filtered schemes
    const categories = [...new Set(filteredSchemes.map(s => getCategory(s.scheme_type)).filter(Boolean))];
    return categories.sort();
  };

  const getFilteredFundCategories = () => {
    let filteredSchemes = schemes;
    
    // Apply Direct/Regular plan filter first
    if (optionFilter === "direct") {
      filteredSchemes = filteredSchemes.filter(s => (s.scheme_name || "").toUpperCase().includes("DIRECT"));
    } else if (optionFilter === "regular") {
      filteredSchemes = filteredSchemes.filter(s => !(s.scheme_name || "").toUpperCase().includes("DIRECT"));
    }
    
    // Filter by selected categories
    if (selectedCategories.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => {
        const derivedCat = getCategory(scheme.scheme_type);
        return selectedCategories.includes(derivedCat);
      });
    }
    
    // Filter by selected AMCs
    if (selectedAmcs.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => 
        selectedAmcs.includes(scheme.amc_code)
      );
    }
    
    // Extract unique fund categories from filtered schemes
    const categories = [...new Set(filteredSchemes.map(s => s.category).filter(Boolean))];
    return categories.sort();
  };

  const getFilteredAmcs = () => {
    let filteredSchemes = schemes;
    
    // Apply Direct/Regular plan filter first
    if (optionFilter === "direct") {
      filteredSchemes = filteredSchemes.filter(s => (s.scheme_name || "").toUpperCase().includes("DIRECT"));
    } else if (optionFilter === "regular") {
      filteredSchemes = filteredSchemes.filter(s => !(s.scheme_name || "").toUpperCase().includes("DIRECT"));
    }
    
    // Filter by selected categories
    if (selectedCategories.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => {
        const derivedCat = getCategory(scheme.scheme_type);
        return selectedCategories.includes(derivedCat);
      });
    }
    
    // Filter by selected fund categories
    if (selectedFundCategories.length > 0) {
      filteredSchemes = filteredSchemes.filter(scheme => 
        selectedFundCategories.includes(scheme.category)
      );
    }
    
    // Extract unique AMCs from filtered schemes
    const amcs = [...new Set(filteredSchemes.map(s => s.amc_code).filter(Boolean))];
    return amcs.sort();
  };

  // Multi-select filter component with "All" option
  const MultiSelectFilter = ({ title, options, selected, onSelectionChange, disabled = false }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    
    // Clean up invalid selections when options change
    useEffect(() => {
      if (selected.length > 0 && options.length > 0) {
        const validSelections = selected.filter(s => options.includes(s));
        if (validSelections.length !== selected.length) {
          onSelectionChange(validSelections);
        }
      }
    }, [options]);
    
    const filteredOptions = options.filter(opt => 
      String(opt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleOption = (option) => {
      if (selected.includes(option)) {
        onSelectionChange(selected.filter(s => s !== option));
      } else {
        onSelectionChange([...selected, option]);
      }
    };

    const selectAll = () => onSelectionChange([...filteredOptions]);
    const clearAll = () => onSelectionChange([]);

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className={`min-w-[140px] justify-between ${selected.length > 0 ? 'border-teal-500 bg-teal-50' : ''} ${disabled ? 'opacity-50' : ''}`}
            disabled={disabled}
          >
            <span className="truncate">
              {selected.length === 0 ? `All ${title}` : `${title} (${selected.length})`}
            </span>
            <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 pl-8"
              />
            </div>
          </div>
          <div className="flex gap-2 p-2 border-b bg-gray-50">
            <Button variant="ghost" size="sm" onClick={selectAll} className="text-sm h-7 flex-1">
              Select All ({filteredOptions.length})
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-sm h-7 flex-1 text-red-600 hover:text-red-700">
              Clear All
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto p-2 space-y-1">
            {options.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No options available</p>
            ) : filteredOptions.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No matches for "{searchTerm}"</p>
            ) : (
              filteredOptions.map((option, idx) => {
                const isSelected = selected.includes(option);
                const displayText = formatOptionText(option);
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-teal-50 border border-teal-200' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleOption(option);
                    }}
                  >
                    <Checkbox 
                      checked={isSelected} 
                      onCheckedChange={() => toggleOption(option)}
                    />
                    <span className="text-sm truncate flex-1">{displayText}</span>
                    {isSelected && <span className="text-teal-600 text-sm">✓</span>}
                  </div>
                );
              })
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t bg-gray-50 text-sm text-gray-600">
              {selected.length} of {options.length} selected
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <TooltipProvider>
      <div className="p-6 space-y-6" data-testid="research-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Research</h1>
            <p className="text-gray-500 mt-1">Browse and analyze mutual fund schemes</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={downloadExcel} 
              disabled={loading || allSortedSchemes.length === 0}
              className="text-green-600 border-green-300 hover:bg-green-50"
              data-testid="download-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            <Button variant="outline" onClick={fetchSchemes} disabled={loading}>
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            {/* Radio buttons for Direct/Regular/Both */}
            <div className="flex items-center gap-6">
              <span className="text-sm font-medium text-gray-700">Option:</span>
              <RadioGroup 
                value={optionFilter} 
                onValueChange={setOptionFilter}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="direct" id="direct" />
                  <Label htmlFor="direct" className="cursor-pointer">Direct Plan</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="regular" id="regular" />
                  <Label htmlFor="regular" className="cursor-pointer">Regular Plan</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="both-plans" />
                  <Label htmlFor="both-plans" className="cursor-pointer">Both</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Main filters row - Reorganized: Tenure first, then Top %, then other filters */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Tenure filter - FIRST */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`min-w-[120px] justify-between ${selectedTenures.length > 0 ? 'border-teal-500 bg-teal-50' : ''}`}
                    data-testid="tenure-filter"
                  >
                    <span className="truncate">
                      {selectedTenures.length === 0 ? "All Tenure" : `Tenure (${selectedTenures.length})`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="flex gap-2 p-2 border-b bg-gray-50">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedTenures(tenureOptions.map(o => o.value))} 
                      className="text-sm h-7 flex-1"
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedTenures([])} 
                      className="text-sm h-7 flex-1 text-red-600 hover:text-red-700"
                    >
                      Clear All
                    </Button>
                  </div>
                  <div className="p-2 space-y-1">
                    {tenureOptions.map((option) => {
                      const isSelected = selectedTenures.includes(option.value);
                      return (
                        <div
                          key={option.value}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-teal-50 border border-teal-200' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTenures(selectedTenures.filter(v => v !== option.value));
                            } else {
                              setSelectedTenures([...selectedTenures, option.value]);
                            }
                          }}
                        >
                          <Checkbox checked={isSelected} onCheckedChange={() => {}} />
                          <span className="text-sm">{option.label}</span>
                          {isSelected && <span className="text-teal-600 text-sm ml-auto">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Top Percentile filter - SECOND */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`min-w-[100px] justify-between ${topPercentile && topPercentile < 100 ? 'border-teal-500 bg-teal-50' : ''}`}
                    data-testid="top-percentile-filter"
                  >
                    <span className="truncate">
                      {topPercentile ? `Top ${topPercentile}%` : "Top %"}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-44 p-0" align="start">
                  <div className="p-2 space-y-1">
                    {topPercentileOptions.map((option) => {
                      const isSelected = topPercentile === option.value;
                      return (
                        <div
                          key={option.value}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-teal-50 border border-teal-200' : ''}`}
                          onClick={() => handleTopPercentileChange(option.value)}
                          data-testid={`top-percentile-${option.value}`}
                        >
                          <span className="text-sm flex-1">{option.label}</span>
                          {isSelected && <span className="text-teal-600 text-sm">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="p-2 border-t bg-gray-50 text-sm text-gray-500">
                    Filters by XIRR performance
                  </div>
                </PopoverContent>
              </Popover>

              {/* Multi-select Category filter (Equity/Debt/Hybrid) */}
              <MultiSelectFilter
                title="Category"
                options={getFilteredCategories()}
                selected={selectedCategories}
                onSelectionChange={setSelectedCategories}
              />

              {/* Multi-select Fund Category filter (AMFI) */}
              <MultiSelectFilter
                title="Fund Category"
                options={getFilteredFundCategories()}
                selected={selectedFundCategories}
                onSelectionChange={setSelectedFundCategories}
              />
              
              {/* Multi-select AMC filter */}
              <MultiSelectFilter
                title="AMC"
                options={getFilteredAmcs()}
                selected={selectedAmcs}
                onSelectionChange={setSelectedAmcs}
              />

              {/* Scheme Name Search */}
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by scheme name, ISIN..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchSchemes()}
                  />
                </div>
              </div>

              <Button 
                variant="outline" 
                onClick={clearFilters}
                className={activeFilterCount > 0 ? "border-red-300 text-red-600 hover:bg-red-50" : ""}
              >
                <X className="w-4 h-4 mr-1" />
                Clear {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-teal-600" />
                Scheme Master
              </CardTitle>
            </div>
            {/* Scheme Breakdown Stats */}
            <div className="flex flex-wrap gap-4 mt-3 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg border border-teal-200">
                <span className="font-medium text-teal-700">Showing:</span>
                <span className="font-bold text-teal-800">{totalFiltered.toLocaleString()}</span>
                <span className="text-teal-600">{optionFilter === "direct" ? "Direct Plan" : optionFilter === "regular" ? "Regular Plan" : "All"} schemes</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
                <span className="font-medium text-green-700">Growth (categorized):</span>
                <span className="font-bold text-green-800">{schemeBreakdown.growth_with_category.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                <span className="font-medium text-blue-700">IDCW (categorized):</span>
                <span className="font-bold text-blue-800">{schemeBreakdown.idcw_with_category.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <span className="font-medium text-gray-700">Total with category:</span>
                <span className="font-bold text-gray-800">{schemeBreakdown.total_with_category.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
                <span className="font-medium text-amber-700">Missing category:</span>
                <span className="font-bold text-amber-800">{schemeBreakdown.missing_category.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
                <span className="font-medium text-slate-700">Total in DB:</span>
                <span className="font-bold text-slate-800">{totalAllSchemes.toLocaleString()}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : schemes.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery || selectedAmcs.length > 0 || selectedCategories.length > 0
                  ? "No schemes match your filters" 
                  : "No schemes found. Upload BSE Scheme Master in Masters page."}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="min-w-[120px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("isin")}
                        >
                          <span className="flex items-center">ISIN <SortIcon columnKey="isin" /></span>
                        </TableHead>
                        <TableHead 
                          className="min-w-[80px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("amfi_code")}
                        >
                          <span className="flex items-center">AMFI Code <SortIcon columnKey="amfi_code" /></span>
                        </TableHead>
                        <TableHead 
                          className="min-w-[160px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("fund_category")}
                        >
                          <span className="flex items-center">Category <SortIcon columnKey="fund_category" /></span>
                        </TableHead>
                        <TableHead 
                          className="min-w-[300px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("scheme_name")}
                        >
                          <span className="flex items-center">Scheme Name <SortIcon columnKey="scheme_name" /></span>
                        </TableHead>
                        <TableHead 
                          className="min-w-[100px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("start_date")}
                        >
                          <span className="flex items-center">Start Date <SortIcon columnKey="start_date" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("tenure")}
                        >
                          <span className="flex items-center justify-end">Tenure <SortIcon columnKey="tenure" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("face_value")}
                        >
                          <span className="flex items-center justify-end">Face Value <SortIcon columnKey="face_value" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right min-w-[100px] cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSort("nav")}
                        >
                          <span className="flex items-center justify-end">Cur NAV <SortIcon columnKey="nav" /></span>
                        </TableHead>
                        {/* XIRR Header Group */}
                        <TableHead colSpan={3} className="text-center bg-teal-600 text-white border-l border-teal-700">
                          <span className="font-semibold">XIRR %</span>
                        </TableHead>
                        {/* Absolute Header Group */}
                        <TableHead colSpan={3} className="text-center bg-indigo-600 text-white border-l border-indigo-700">
                          <span className="font-semibold">Absolute(Avg) %</span>
                        </TableHead>
                      </TableRow>
                      {/* Sub-header row for time periods */}
                      <TableRow className="bg-gray-50">
                        <TableHead colSpan={8} className="p-0"></TableHead>
                        {/* XIRR sub-columns */}
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-teal-50 bg-teal-50 border-l border-teal-200"
                          onClick={() => handleSort("xirr")}
                        >
                          <span className="flex items-center justify-end">Inception <SortIcon columnKey="xirr" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-teal-50 bg-teal-50"
                          onClick={() => handleSort("xirr3y")}
                          data-testid="sort-xirr3y"
                        >
                          <span className="flex items-center justify-end">3 Year <SortIcon columnKey="xirr3y" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-teal-50 bg-teal-50"
                          onClick={() => handleSort("xirr5y")}
                        >
                          <span className="flex items-center justify-end">5 Year <SortIcon columnKey="xirr5y" /></span>
                        </TableHead>
                        {/* Absolute sub-columns */}
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-indigo-50 bg-indigo-50 border-l border-indigo-200"
                          onClick={() => handleSort("absoluteReturns")}
                        >
                          <span className="flex items-center justify-end">Inception <SortIcon columnKey="absoluteReturns" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-indigo-50 bg-indigo-50"
                          onClick={() => handleSort("abs3y")}
                        >
                          <span className="flex items-center justify-end">3 Year <SortIcon columnKey="abs3y" /></span>
                        </TableHead>
                        <TableHead 
                          className="text-right text-sm cursor-pointer hover:bg-indigo-50 bg-indigo-50"
                          onClick={() => handleSort("abs5y")}
                        >
                          <span className="flex items-center justify-end">5 Year <SortIcon columnKey="abs5y" /></span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedSchemes.map((scheme, index) => {
                        return (
                          <TableRow key={`${scheme.unique_no || scheme.isin}-${index}`}>
                            {/* ISIN with tooltip */}
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help text-blue-600 font-mono text-sm flex items-center gap-1">
                                    {scheme.isin || "-"}
                                    <Info className="w-3 h-3 text-gray-400" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                  <div className="space-y-1 text-sm">
                                    <p><strong>Scheme Type:</strong> {scheme.scheme_type || "-"}</p>
                                    <p><strong>Channel Partner:</strong> {scheme.channel_partner_code || scheme.scheme_code || "-"}</p>
                                    <p><strong>AMC Code:</strong> {scheme.amc_code || "-"}</p>
                                    <p><strong>RTA Agent Code:</strong> {scheme.rta_agent_code || "-"}</p>
                                    {scheme.updated_at && (
                                      <p className="text-sm text-gray-500 pt-1 border-t border-gray-200 mt-1">
                                        BSE Updated: {new Date(scheme.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            
                            {/* AMFI Code */}
                            <TableCell>
                              <span className="font-mono text-sm text-gray-700">{scheme.amfi_code || "-"}</span>
                            </TableCell>
                            
                            {/* Category (from AMFI) - shows short version after the hyphen */}
                            <TableCell>
                              {scheme.category ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-sm text-gray-600 truncate max-w-[160px] block cursor-help">
                                      {(() => {
                                        const shortCat = getShortCategory(scheme.category);
                                        return shortCat && shortCat.length > 22 
                                          ? shortCat.substring(0, 22) + "..." 
                                          : shortCat;
                                      })()}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    <span>{scheme.category}</span>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* Scheme Name */}
                            <TableCell className="font-medium">
                              {scheme.scheme_name || "-"}
                            </TableCell>
                            
                            {/* Start Date */}
                            <TableCell className="text-gray-600">
                              {formatDate(scheme.start_date)}
                            </TableCell>
                            
                            {/* Tenure */}
                            <TableCell className="text-right text-gray-600">
                              {scheme.tenureDisplay}
                            </TableCell>
                            
                            {/* Face Value */}
                            <TableCell className="text-right">
                              {scheme.face_value ? (
                                scheme.nav_start_date && scheme.start_date && scheme.nav_start_date !== scheme.start_date ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-red-600 font-semibold cursor-help">
                                        ₹{scheme.face_value.toFixed(2)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="max-w-xs">
                                      <p className="text-xs">NAV data available from <strong>{formatDate(scheme.nav_start_date)}</strong></p>
                                      <p className="text-xs text-gray-400 mt-0.5">Scheme start date: {formatDate(scheme.start_date)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span>₹{scheme.face_value.toFixed(2)}</span>
                                )
                              ) : "-"}
                            </TableCell>
                            
                            {/* Current NAV - with 3Y and 5Y in tooltip */}
                            <TableCell className="text-right">
                              {scheme.current_nav ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-semibold text-green-600 cursor-help">
                                      ₹{scheme.current_nav.toFixed(2)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs">
                                    <div className="space-y-1 text-sm">
                                      <p><strong>Current NAV:</strong> ₹{scheme.current_nav.toFixed(2)}</p>
                                      {scheme.current_nav_date && (
                                        <p><strong>Date:</strong> {scheme.current_nav_date}</p>
                                      )}
                                      {/* Grandfathered NAV for LTCG */}
                                      {scheme.nav_grandfathered && (
                                        <>
                                          <hr className="my-1 border-gray-300" />
                                          <p className="font-medium text-amber-700">LTCG Grandfathering:</p>
                                          <p><strong>31-Jan-2018 NAV:</strong> ₹{scheme.nav_grandfathered.toFixed(2)}</p>
                                        </>
                                      )}
                                      {/* Historical NAVs for 3Y and 5Y */}
                                      {(scheme.nav_3y || scheme.nav_5y) && (
                                        <>
                                          <hr className="my-1 border-gray-300" />
                                          <p className="font-medium text-teal-700">Historical NAVs:</p>
                                          {scheme.nav_3y && (
                                            <p><strong>3Y ago:</strong> ₹{scheme.nav_3y.toFixed(2)} {scheme.nav_3y_date && <span className="text-gray-400">({scheme.nav_3y_date})</span>}</p>
                                          )}
                                          {scheme.nav_5y && (
                                            <p><strong>5Y ago:</strong> ₹{scheme.nav_5y.toFixed(2)} {scheme.nav_5y_date && <span className="text-gray-400">({scheme.nav_5y_date})</span>}</p>
                                          )}
                                        </>
                                      )}
                                      {scheme.nav_updated_at && (
                                        <p className="text-sm text-gray-400 pt-1 border-t border-gray-200">
                                          NAV Updated: {new Date(scheme.nav_updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* XIRR Group - Inception */}
                            <TableCell className="text-right bg-teal-50/30 border-l border-teal-100">
                              {scheme.xirr !== null ? (
                                <span className={`font-semibold ${scheme.xirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {scheme.xirr.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* XIRR Group - 3 Year */}
                            <TableCell className="text-right bg-teal-50/30">
                              {scheme.xirr3y !== null ? (
                                <span className={`font-semibold ${scheme.xirr3y >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {scheme.xirr3y.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* XIRR Group - 5 Year */}
                            <TableCell className="text-right bg-teal-50/30">
                              {scheme.xirr5y !== null ? (
                                <span className={`font-semibold ${scheme.xirr5y >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {scheme.xirr5y.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* Absolute Group - Inception (shows Avg, tooltip shows Total) */}
                            <TableCell className="text-right bg-indigo-50/30 border-l border-indigo-100">
                              {scheme.absoluteReturnsAvg !== null ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`font-semibold cursor-help ${scheme.absoluteReturnsAvg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {scheme.absoluteReturnsAvg.toFixed(2)}%
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-sm">Total Absolute: <strong>{scheme.absoluteReturns?.toFixed(2)}%</strong></p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* Absolute Group - 3 Year (shows Avg, tooltip shows Total) */}
                            <TableCell className="text-right bg-indigo-50/30">
                              {scheme.abs3yAvg !== null ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`font-semibold cursor-help ${scheme.abs3yAvg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {scheme.abs3yAvg.toFixed(2)}%
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-sm">Total Absolute (3Y): <strong>{scheme.abs3yTotal?.toFixed(2)}%</strong></p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            
                            {/* Absolute Group - 5 Year (shows Avg, tooltip shows Total) */}
                            <TableCell className="text-right bg-indigo-50/30">
                              {scheme.abs5yAvg !== null ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`font-semibold cursor-help ${scheme.abs5yAvg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {scheme.abs5yAvg.toFixed(2)}%
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-sm">Total Absolute (5Y): <strong>{scheme.abs5yTotal?.toFixed(2)}%</strong></p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} {optionFilter === "both" ? "all" : optionFilter === "direct" ? "Direct Plan" : "Regular Plan"} schemes
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-600">Page</span>
                      <Input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={page}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val >= 1 && val <= totalPages) {
                            setPage(val);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 1 && val <= totalPages) {
                              setPage(val);
                            } else if (val > totalPages) {
                              setPage(totalPages);
                            } else if (val < 1 || isNaN(val)) {
                              setPage(1);
                            }
                          }
                        }}
                        className="w-16 h-8 text-center text-sm"
                        data-testid="pagination-input"
                      />
                      <span className="text-sm text-gray-600">of {totalPages}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                      data-testid="pagination-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default Research;
